import {_} from 'lodash';
import * as ko from "knockout";
import {
    AvailableLanguages,
    EMPTY_STRING,
    LanguageId,
    PLEASE_WAIT_MESSAGE,
    REQUEST_MESSAGE,
    UPLOADING_MESSAGE
} from "../../../constants/Language";
import {IdName, IMessageData, ISyncField} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {ButtonManager} from "../../../util/ButtonManager";
import {getBackButton, getCancelButton, getContinueButton, getDiscardButton} from "../../../constants/RouteDefinitions";
import {
    createErrorMessage,
    createErrorString,
    getObjectDefinition,
    getObjectType,
    isNullOrUndefined,
    isNullOrUndefinedOrEmptyString,
    isPartOf
} from "../../../util/misc";
import {AVAILABLE_CSV_DELIMITERS, MAX_CONCURRENT_REQUESTS, ObjectTypeId, SPINNER} from "../../../constants";
import {nsSyncFields} from "../../../req/nsSyncFields";
import PromiseRequest from "../../../req/PromiseRequest";
import {ISyncfieldsSuccess} from "../index";
import {global, lm} from "../../../appContext";
import {nsForm} from "../../form/nsForm";
import {ImportFormatId} from "../../../constants/ImportFormats";
import {TServiceName} from "../../../constants/ApiServices";
import str2Bool = nsForm.str2Bool;
import importHtml from './import.html?raw';

export const template = importHtml

type TStep = 'init' | 'file' | 'upload' | 'parse' | 'review' | 'process' | 'finish'

const AllSteps: TStep[] = ['init', 'file', 'upload', 'parse', 'review', 'process', 'finish']

const MAX_DISPLAYNAME_LENGTH = 256
const MAX_DESCRIPTION_LENGTH = 4096

const COMPARE_KEYS: (keyof ISyncField)[] = [
    'core',
    'description',
    'displayname',
    'parameters',
]

type TMatchBy = 'syncfieldid' | 'name'

type IChangedSyncField = { changed: ISyncField, original: ISyncField };

interface ISelectableNewGroup {
    items: ISyncField[]
    selected: KnockoutObservable<boolean>
}

interface ISelectableChangedGroup {
    items: IChangedSyncField[]
    selected: KnockoutObservable<boolean>
}

type TDelimiter = typeof AVAILABLE_CSV_DELIMITERS[number]

export class viewModel {
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false)
    private readonly step: KnockoutObservable<TStep | null> = ko.observable(null)
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    private readonly pageButtons = new ButtonManager()
    private readonly file: KnockoutObservable<File | null> = ko.observable(null)
    private readonly isDragOver: KnockoutObservable<boolean> = ko.observable(false)
    private dragEnterCount = 0
    private uploadResult!: string
    private readonly percentComplete: KnockoutObservable<number | null> = ko.observable(null)
    private fileReader!: FileReader
    private availableImportFormats!: IdName[]
    private requests: PromiseRequest[] = []
    private successes!: ISyncField[]
    private errors!: { reason: string, syncfield: ISyncField }[]
    private defaults: ISyncfieldsSuccess[] = []
    private parsedSyncfields!: ISyncField[]
    private parsedSyncfieldsUnchanged!: ISyncField[]
    private hasReviewData = false

    private readonly matchBy: KnockoutObservable<TMatchBy> = ko.observable<TMatchBy>('name')
    private readonly treatEmptyAsNull: KnockoutObservable<boolean> = ko.observable(true)

    private readonly parsedSyncfieldsNew: KnockoutObservableArray<ISelectableNewGroup> = ko.observableArray<ISelectableNewGroup>([])
    private readonly parsedSyncfieldsChanged: KnockoutObservableArray<ISelectableChangedGroup> = ko.observableArray<ISelectableChangedGroup>([])

    private readonly includeNew: KnockoutComputed<boolean>
    private readonly includeChanged: KnockoutComputed<boolean>
    private readonly isNewIndeterminate: KnockoutComputed<boolean>
    private readonly isChangedIndeterminate: KnockoutComputed<boolean>
    private readonly newGroupCount: KnockoutComputed<number>
    private readonly changedGroupCount: KnockoutComputed<number>

    private delimiter: KnockoutObservable<TDelimiter> = ko.observable<TDelimiter>(',')

    private lastProcessedRow: KnockoutObservable<IChangedSyncField | null> = ko.observable(null)

    constructor() {
        this.newGroupCount = ko.pureComputed(() => this.parsedSyncfieldsNew().length)
        this.changedGroupCount = ko.pureComputed(() => this.parsedSyncfieldsChanged().length)

        this.includeNew = ko.computed({
            read: () => {
                const groups = this.parsedSyncfieldsNew()
                return groups.length > 0 && groups.filter(g => g.selected()).length === groups.length
            },
            write: (checked: boolean) => {
                this.parsedSyncfieldsNew().forEach(g => g.selected(checked))
            },
        })

        this.includeChanged = ko.computed({
            read: () => {
                const groups = this.parsedSyncfieldsChanged()
                return groups.length > 0 && groups.filter(g => g.selected()).length === groups.length
            },
            write: (checked: boolean) => {
                this.parsedSyncfieldsChanged().forEach(g => g.selected(checked))
            },
        })

        this.isNewIndeterminate = ko.pureComputed(() => {
            const groups = this.parsedSyncfieldsNew()
            const selectedCount = groups.filter(g => g.selected()).length
            return selectedCount > 0 && selectedCount < groups.length
        })

        this.isChangedIndeterminate = ko.pureComputed(() => {
            const groups = this.parsedSyncfieldsChanged()
            const selectedCount = groups.filter(g => g.selected()).length
            return selectedCount > 0 && selectedCount < groups.length
        })

        this.kosub.subscribe(this.step, (step) => {
            if (step !== null) {
                this.onStepChange(step)
            }
        })

        this.kosub.subscribe(this.matchBy, () => {
            if (this.hasReviewData) {
                this.recategorize()
            }
        })

        this.kosub.subscribe(this.treatEmptyAsNull, () => {
            if (this.hasReviewData) {
                this.recategorize()
            }
        })

        this.pageButtons.add(getContinueButton({
            onClick: () => {
                const currentStep = this.step()
                if (currentStep === null) {
                    return
                }
                const index = AllSteps.indexOf(currentStep)
                const next = AllSteps[index + 1]
                if (next) {
                    this.step(next)
                }
            },
            enabled: () => {
                switch (this.step()) {
                    case "file":
                        return !!this.file()
                    case "review":
                        return this.parsedSyncfieldsNew().filter(g => g.selected()).length > 0
                            || this.parsedSyncfieldsChanged().filter(g => g.selected()).length > 0
                    default:
                        return false
                }
            },
            validator: () => this.isStep('file', 'review'),
        }))

        this.pageButtons.add(getDiscardButton({
            label: 'start over',
            onClick: () => {
                this.messageData(null)
                this.file(null)
                this.isDragOver(false)
                this.dragEnterCount = 0
                this.fileReader?.abort()
                this.step('init')
            },
            validator: () => this.isStep('finish'),
        }))

        const cancelBtn = getCancelButton('systemfieldsdetails', {
            validator: () => !this.isStep('finish')
        })
        if (cancelBtn) {
            this.pageButtons.add(cancelBtn)
        }

        const backBtn = getBackButton('systemfieldsdetails', {
            validator: () => this.isStep('finish')
        })
        if (backBtn) {
            this.pageButtons.add(backBtn)
        }

        this.step('init')
    }

    private onStepChange(step: TStep) {
        switch (this.step()) {

            case 'init': {
                const req = global.getPromiseRequest('getimportformats')
                this.requests.push(req)
                req
                    .request()
                    .then(data => this.availableImportFormats = data)
                    .then(() => {
                        this.isReady(false)
                        this.step('file')
                    })
                    .catch((reason: string) => {
                        this.messageData(createErrorMessage(String(reason)))
                    })
                break
            }

            case 'file': {
                this.isReady(true)
                break
            }

            case 'upload': {
                this.percentComplete(0)
                this.isReady(false)
                this.step('upload')
                this.messageData({message: REQUEST_MESSAGE});
                this.fileReader = new FileReader();
                this.fileReader.onprogress = (e: ProgressEvent) => {
                    this.percentComplete(e.loaded / e.total)
                }
                this.fileReader.onload = (e: ProgressEvent) => {
                    this.uploadResult = (e.target as FileReader).result?.toString() ?? ''
                    this.messageData(null)
                    this.step('parse')
                };
                const file = this.file();
                if (!file) {
                    break;
                }
                this.fileReader.readAsText(file, 'UTF-8');
                break
            }

            case 'parse': {
                this.messageData({message: PLEASE_WAIT_MESSAGE});
                this.isReady(false)
                this.hasReviewData = false
                this.defaults = []

                try {
                    const d = this.delimiter()
                    // const regexStr = `^(?:""(?=,))|(?<=,)(?:"")?(?=,)|".*?(?:""|[^"])"(?=,|$)|[^,]+|^[^,]*|(?<=,)$`
                    const regexStr = `^(?:""(?=${d}))|(?<=${d})(?:"")?(?=${d})|".*?(?:""|[^"])"(?=${d}|$)|[^${d}]+|^[^${d}]*|(?<=${d})$`
                    const regex = new RegExp(regexStr, 'g')
                    const _table = this.uploadResult
                        .replace(/\r/gm, '\n')
                        .split(/$\n/gm)
                        .filter(Boolean)
                        .map(row => row.match(regex))
                        .filter((row): row is RegExpMatchArray => !!row)
                        .map(row => row
                            .map(s => {
                                if (s.startsWith('"') && s.endsWith('"')) {
                                    s = s.slice(1, s.length - 1)
                                }
                                return s
                            })
                            .map(s => {
                                return s.replace(/""/g, '"')
                            })
                        )

                    const _headings = _table[0]
                    if (!_headings) {
                        throw 'Error: CSV is malformed.\n\nExpected a headings row but file was empty.'
                    }
                    const _rows = _table.slice(1)

                    const keyIndexes: Partial<Record<string, number[]>> = {}
                    _headings.forEach((key: string, index: number) => {
                        if (!keyIndexes[key]) {
                            keyIndexes[key] = []
                        }
                        keyIndexes[key].push(index)
                    })

                    const getCsvValue = (obj: string[], key: string, fn: Function = String, index = 0) => {
                        const i = keyIndexes[key]?.[index]
                        if (i === undefined) {
                            return null
                        }
                        let val = obj[i];
                        if (val === undefined) {
                            return null;
                        }
                        if (val == 'null') {
                            return null
                        }
                        switch (fn) {
                            case String: {
                                val = val
                                    .replace(/""/gm, '"')
                                    .replace(/\\n/gm, '\n')
                                break
                            }
                            case Number: {
                                if (isNaN(fn(val))) {
                                    return null
                                }
                                break
                            }
                            case Boolean: {
                                if (typeof val == 'string') {
                                    return str2Bool(val)
                                }
                                break
                            }
                        }
                        return fn(val)
                    }

                    const fnValidatorLength = (str: string, max: number, type: string, rowNumber: number, row: any): boolean => {
                        if ((str?.length ?? 0) > max) {
                            throw (`Error: DisplayName is not valid.
                                    
Expected ${type} to be max ${max} characters but got ${str.length} characters.

<b>Row #${rowNumber}</b>: ${String(row)}

<b>${type} (${str.length} characters)</b>: ${String(str)}`)
                        }
                        return true
                    }

                    let parsedSyncfields: ISyncField[] = _(_rows)
                        .map((o: string[], index: number) => {
                            const rowNumber = index + 2 // +1 for zero indexed and +1 for headings row
                            if (o.length != _headings.length) {
                                throw (`Error: CSV is malformed.
                                
Expected row #${rowNumber} to be length ${_headings.length} but got length ${o.length}.

Headings (${_headings.length}):
${_headings}

Row #${rowNumber} (${o.length}):
${o}`)
                            }
                            const v = getCsvValue.bind(this, o)
                            return (keyIndexes['languageid'] ?? []).map((langIndex, i) => {
                                const displayName = v('displayname', String, i) ?? ''
                                const description = v('description', String, i) ?? ''
                                fnValidatorLength(displayName, MAX_DISPLAYNAME_LENGTH, 'DisplayName', rowNumber, o)
                                fnValidatorLength(description, MAX_DESCRIPTION_LENGTH, 'Description', rowNumber, o)
                                return {
                                    // these attributes are common for all translations
                                    // Missing CSV columns (e.g. translation-only exports) yield null;
                                    // API JObjectField expects non-nullable bools (null → false).
                                    ignorefilter: v('ignorefilter', Boolean) ?? false,
                                    issynced: v('issynced', Boolean) ?? false,
                                    name: v('name'),
                                    objecttypeid: v('objecttypeid', Number),
                                    required: v('required', Boolean) ?? false,
                                    syncfieldid: v('syncfieldid', Number),
                                    value: null, // value should never have a value

                                    // these attributes are specific for each translation
                                    core: v('core', Boolean, i),
                                    description: v('description', String, i),
                                    displayname: displayName,
                                    importformatid: v('importformatid', Number, i),
                                    languageid: v('languageid', Number, i),
                                    parameters: v('parameters', String, i),
                                    syncfielddisplaynameid: v('syncfielddisplaynameid', Number, i),

                                    // these attributes are unmutable (and populated later)
                                    fieldtype: null,
                                    isoverridablebylevel: null,
                                    maxlength: null,
                                    metaidentifier: null,
                                    referencefieldid: null,
                                    referenceobjecttypeid: null,
                                } as unknown as ISyncField
                            })
                        })
                        .flatten()
                        .value()

                    // --- DOWNLOAD DEFAULT SYNCFIELDS -----------------------------------------------------------------

                    const parameterIdsForGetsyncfields: number[][] = (() => {
                        const ret: Set<string> = new Set()
                        parsedSyncfields.forEach((o) => {
                            // add each id combo as a string to let Set do its uniqueness
                            ret.add([o.objecttypeid, LanguageId.Norwegian, o.importformatid].join(','))
                            // also add Norwegian to the download (which will be the default if there isn't a translation)
                            ret.add([o.objecttypeid, o.languageid, o.importformatid].join(','))
                        })
                        return Array
                            .from(ret)
                            .map(str => str.split(','))
                            .map(arr => arr.map(Number))
                    })()

                    const numTotal = parameterIdsForGetsyncfields.length

                    const doNext = (): Promise<void> => {
                        const numLoaded = this.defaults.length

                        this.percentComplete(numLoaded / numTotal)
                        this.messageData({message: `((loading data|td)) [${numLoaded}/${numTotal}] ${SPINNER}`})
                        if (!parameterIdsForGetsyncfields.length) {
                            return Promise.resolve()
                        }
                        const tuple = parameterIdsForGetsyncfields.shift()
                        if (!tuple) {
                            return Promise.resolve()
                        }
                        const [objectTypeId, languageId, importFormatId] = tuple
                        const language = AvailableLanguages.find(o => o.id == languageId)
                        const importFormat = this.availableImportFormats.find(o => o.id == importFormatId)
                        if (!language || !importFormat) {
                            return doNext()
                        }
                        if (objectTypeId === undefined) {
                            return doNext();
                        }
                        const reqObj = nsSyncFields.getSyncFields(objectTypeId, languageId, importFormatId)
                        this.requests.push(reqObj.promise as PromiseRequest)
                        return reqObj.request
                            .then((data: ISyncField[]) => {
                                this.defaults.push({
                                    l: language,
                                    o: getObjectType(objectTypeId as ObjectTypeId),
                                    i: importFormat,
                                    syncfields: data,
                                })
                                return doNext()
                            })
                    }

                    const all = []
                    for (let i = 0; i < Math.min(parameterIdsForGetsyncfields.length, MAX_CONCURRENT_REQUESTS); i++) {
                        all.push(doNext())
                    }

                    Promise.all(all)
                        .then(() => {
                            this.parsedSyncfields = parsedSyncfields.map(sf => {
                                const sf_default = this.getDefaultSyncfield(sf) ?? this.getDefaultSyncfield(sf, LanguageId.Norwegian)
                                const sf_unmutable: Partial<ISyncField> = _.pick(sf_default ?? {},
                                    'fieldtype',
                                    'isoverridablebylevel',
                                    'maxlength',
                                    'metaidentifier',
                                    'referencefieldid',
                                    'referenceobjecttypeid',

                                    'importformatid',
                                    'languageid',
                                    'syncfielddisplaynameid',
                                )
                                const ret = _.defaults(sf_unmutable, sf);
                                if (isNullOrUndefinedOrEmptyString(ret.fieldtype)) {
                                    ret.fieldtype = 'String'
                                }
                                // CSV has no int columns; parse leaves null — JObjectField requires ints
                                return this.applyJObjectFieldIntDefaults(ret)
                            })
                        })
                        .then(() => {
                            this.recategorize()
                            this.hasReviewData = true
                            this.messageData(null)
                            this.step('review')
                        })
                        .catch((reason: string) => {
                            this.isReady(false)
                            this.step(null)
                            this.messageData(createErrorMessage(String(reason)))
                        })

                } catch (err) {
                    this.step(null)
                    this.messageData(createErrorMessage(String(err)))
                }

                break
            }

            case 'review': {
                this.isReady(true)
                break
            }

            case 'process': {
                this.isReady(false)
                this.messageData({message: UPLOADING_MESSAGE})

                this.successes = []
                this.errors = []

                const arrUpdateAndCreate: (() => Promise<void>)[] = []

                this.parsedSyncfieldsNew()
                    .filter(g => g.selected())
                    .forEach(group => {
                        arrUpdateAndCreate.push(() => {
                            const arr = _.clone(group.items)
                            const sf = _.cloneDeep(arr[0])
                            sf.syncfieldid = 0
                            sf.syncfielddisplaynameid = 0
                            sf.displayname = ''
                            sf.description = ''
                            sf.core = false
                            sf.fieldtype = '' as any
                            sf.referencefieldid = 0
                            sf.referenceobjecttypeid = 0
                            sf.parameters = ''
                            sf.isoverridablebylevel = 16
                            sf.maxlength = 0
                            const p = new PromiseRequest('createsyncfield')
                            this.requests.push(p)
                            return p.request({jsyncfield: JSON.stringify(sf)})
                                .then((data: [ISyncField]) => {
                                    const sf = data[0];
                                    this.successes.push(sf)
                                    arr.forEach((sfd: ISyncField) => {
                                        arrUpdateAndCreate.push(() => {
                                            const p = new PromiseRequest('createsyncfielddisplayname')
                                            this.requests.push(p)
                                            const _sfd = _.clone(sfd)
                                            _sfd.syncfieldid = sf.syncfieldid
                                            _sfd.syncfielddisplaynameid = 0
                                            _sfd.isoverridablebylevel = sf.isoverridablebylevel
                                            _sfd.maxlength = sf.maxlength
                                            _sfd.referencefieldid = 0
                                            _sfd.referenceobjecttypeid = 0
                                            if (isNullOrUndefinedOrEmptyString(_sfd.fieldtype)) {
                                                _sfd.fieldtype = 'String'
                                            }
                                            this.applyJObjectFieldIntDefaults(_sfd)

                                            return p.request({jdisplayname: JSON.stringify(_sfd)})
                                                .then(sf => {
                                                    this.successes.push(sf)
                                                })
                                                .catch((reason: string) => {
                                                    this.errors.push({
                                                        reason: reason,
                                                        syncfield: sf,
                                                    })
                                                })
                                        })
                                    })
                                })
                                .catch((reason: string) => {
                                    this.errors.push({
                                        reason: reason,
                                        syncfield: sf,
                                    })
                                })
                        })
                    })

                this.parsedSyncfieldsChanged()
                    .filter(g => g.selected())
                    .forEach(group => {
                        group.items.forEach((o: IChangedSyncField) => {
                            arrUpdateAndCreate.push(() => {
                                const sf = this.applyJObjectFieldIntDefaults({
                                    ...o.changed,
                                    syncfieldid: o.original.syncfieldid,
                                    syncfielddisplaynameid: o.original.syncfielddisplaynameid,
                                    // Prefer live syncfield ints from getsyncfields when CSV left nulls
                                    isoverridablebylevel: o.changed.isoverridablebylevel
                                        ?? o.original.isoverridablebylevel,
                                    maxlength: o.changed.maxlength ?? o.original.maxlength,
                                    referencefieldid: o.changed.referencefieldid
                                        ?? o.original.referencefieldid,
                                    referenceobjecttypeid: o.changed.referenceobjecttypeid
                                        ?? o.original.referenceobjecttypeid,
                                })
                                if (isNullOrUndefinedOrEmptyString(sf.fieldtype)) {
                                    sf.fieldtype = 'String'
                                }
                                const serviceName: TServiceName = sf.syncfielddisplaynameid
                                    ? 'updatesyncfielddisplayname'
                                    : 'createsyncfielddisplayname'
                                const p = new PromiseRequest(serviceName)
                                this.requests.push(p)
                                return p.request({jdisplayname: JSON.stringify(sf)})
                                    .then(sf => {
                                        this.successes.push(sf)
                                    })
                                    .catch((reason: string) => {
                                        this.errors.push({
                                            reason: reason,
                                            syncfield: sf,
                                        })
                                    })
                            })
                        })
                    })

                const doNext = (): Promise<void> => {
                    const fn = arrUpdateAndCreate.shift()
                    if (!fn) {
                        return Promise.resolve()
                    }
                    return fn().then(() => doNext())
                }


                doNext().then(() => {
                    global.cacheManager.removeCache('getsyncfields')
                    this.messageData(null)
                    this.step('finish')
                })

                break
            }

            case 'finish': {
                this.isReady(true)
                break
            }

        }
    }

    /** Split parsed rows into new/changed/unchanged groups using current match + empty≈null options. */
    private recategorize(): void {
        const changed: IChangedSyncField[] = []
        this.parsedSyncfieldsUnchanged = []
        const news: ISyncField[] = []

        this.parsedSyncfields.forEach(sf => {
            const sf_default = this.getDefaultSyncfield(sf) ?? this.getDefaultSyncfield(sf, LanguageId.Norwegian)
            if (!sf_default) {
                news.push(sf)
                return
            }
            const isChanged = COMPARE_KEYS.some(k => !this.valuesEqual(sf_default[k], sf[k]))
            if (isChanged) {
                changed.push({
                    changed: {
                        ...sf,
                        syncfieldid: sf_default.syncfieldid,
                        syncfielddisplaynameid: sf_default.syncfielddisplaynameid,
                    },
                    original: sf_default,
                })
            } else {
                this.parsedSyncfieldsUnchanged.push(sf)
            }
        })

        this.parsedSyncfieldsChanged(
            _(changed)
                .groupBy((o: IChangedSyncField) => o.original.syncfieldid)
                .map((items: IChangedSyncField[]) => ({
                    items,
                    selected: ko.observable(true),
                }))
                .value()
        )

        this.parsedSyncfieldsNew(
            _(news)
                .groupBy((o: ISyncField) => o.name)
                .map((items: ISyncField[]) => ({
                    items,
                    selected: ko.observable(true),
                }))
                .value()
        )
    }

    private valuesEqual(a: unknown, b: unknown): boolean {
        if (this.treatEmptyAsNull()) {
            const normalize = (v: unknown) => (v === '' || v == null) ? null : v
            return normalize(a) === normalize(b)
        }
        return a == b
    }

    private onFileChange(vm: viewModel, e: JQuery.ChangeEvent) {
        const files = (e.target as HTMLInputElement).files;
        this.acceptFile(files?.[0])
    }

    /** Accept a single CSV from the file picker or a drop zone. */
    private acceptFile(file: File | null | undefined): void {
        if (!file) {
            return
        }
        if (!this.isCsvFile(file)) {
            this.messageData(createErrorMessage('invalid value'))
            return
        }
        this.messageData(null)
        this.file(file)
        this.step('upload')
    }

    private isCsvFile(file: File): boolean {
        const name = file.name.toLowerCase()
        return name.endsWith('.csv')
            || file.type === 'text/csv'
            || file.type === 'application/vnd.ms-excel'
    }

    private onDragEnter(vm: viewModel, e: JQuery.DragEvent): boolean {
        if (!this.isFileDrag(e)) {
            return true
        }
        e.preventDefault()
        this.dragEnterCount++
        this.isDragOver(true)
        return false
    }

    private onDragOver(vm: viewModel, e: JQuery.DragEvent): boolean {
        if (!this.isFileDrag(e)) {
            return true
        }
        e.preventDefault()
        const dataTransfer = e.originalEvent?.dataTransfer
        if (dataTransfer) {
            dataTransfer.dropEffect = 'copy'
        }
        return false
    }

    private onDragLeave(vm: viewModel, e: JQuery.DragEvent): boolean {
        if (!this.isFileDrag(e)) {
            return true
        }
        this.dragEnterCount = Math.max(0, this.dragEnterCount - 1)
        if (this.dragEnterCount === 0) {
            this.isDragOver(false)
        }
        return false
    }

    private onDrop(vm: viewModel, e: JQuery.DragEvent): boolean {
        e.preventDefault()
        this.dragEnterCount = 0
        this.isDragOver(false)
        const file = e.originalEvent?.dataTransfer?.files?.[0]
        this.acceptFile(file)
        return false
    }

    private isFileDrag(e: JQuery.DragEvent): boolean {
        const types = e.originalEvent?.dataTransfer?.types
        if (!types) {
            return false
        }
        return Array.from(types).includes('Files')
    }

    private isStep(...step: TStep[]) {
        const currentStep = this.step();
        return !!currentStep && isPartOf(currentStep, ...step)
    }

    private getDefaultSyncfield(sf: Partial<ISyncField>, languageId: LanguageId = sf.languageid as LanguageId): ISyncField | undefined {
        return this.defaults.find(obj =>
            obj.o.id == sf.objecttypeid &&
            obj.l.id == languageId &&
            obj.i.id == sf.importformatid
        )?.syncfields.find(_sf => {
            if (this.matchBy() === 'name') {
                return sf.name == _sf.name
            }
            return sf.syncfieldid == _sf.syncfieldid
        })
    }

    /**
     * CSV translation imports omit syncfield int columns; parse sets null.
     * API JObjectField uses non-nullable ints (same defaults as createsyncfield / backend).
     */
    private applyJObjectFieldIntDefaults(sf: ISyncField): ISyncField {
        if (isNullOrUndefined(sf.isoverridablebylevel)) {
            sf.isoverridablebylevel = 16
        }
        if (isNullOrUndefined(sf.maxlength)) {
            sf.maxlength = 0
        }
        if (isNullOrUndefined(sf.referencefieldid)) {
            sf.referencefieldid = 0
        }
        if (isNullOrUndefined(sf.referenceobjecttypeid)) {
            sf.referenceobjecttypeid = 0
        }
        return sf
    }

    private getObjectType(objecttypeid: number): string {
        const definition = getObjectDefinition(objecttypeid);
        return definition ? lm.t(definition.name) ?? '' : ''
    }

    private getImportFormat(importformatid: number): IdName | undefined {
        return this.availableImportFormats.find(o => o.id == importformatid)
    }

    private getLanguageKey(languageId: LanguageId): string {
        return AvailableLanguages.find(o => o.id == languageId)?.key ?? ''
    }

    private getImportFormatName(importFormatId: ImportFormatId): string {
        return this.getImportFormat(importFormatId)?.name ?? ''
    }

    private isPropertyChanged(key: keyof ISyncField, obj: IChangedSyncField): boolean {
        return !this.valuesEqual(obj.original[key], obj.changed[key])
    }

    private getFormattedValue(val: any): string {
        if (isNullOrUndefined(val)) {
            return `[${String(val)}]`
        }
        if (val === '') {
            return lm.t(EMPTY_STRING) ?? ''
        }
        return val.toString()
    }

    private createErrorString(reason: string): string {
        return createErrorString(reason, '_blank')
    }

    dispose() {
        this.kosub.unsuscribeAll()
        this.includeNew.dispose()
        this.includeChanged.dispose()
        this.isNewIndeterminate.dispose()
        this.isChangedIndeterminate.dispose()
        this.newGroupCount.dispose()
        this.changedGroupCount.dispose()
        this.fileReader?.abort()
        this.requests?.forEach(r => r.abort())
    }
}
